import type { ConversationMessage, PlaceholderAnalysis } from '@/features/AI-documents/types/docGen.types';
import type {
  ChatFlow,
  ChatFlowContext,
  ChatFlowEventResult,
} from '@/features/startTask/preset_task/shared/preset/chatflows/types';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';

/**
 * MY — Beneficial Owner Letters Appendix A/B/C (M32109) chatflow.
 *
 * 문서-뷰 Preset — 설문으로 Appendix 선택 → 선택 양식만 플랫 표시.
 *   input-title → select-entity(skip 가능) → confirm
 *   + Person Lookup (BO / Member / Senior Management ✨)
 *   + Address 입력
 */
export const boAppendixFlow: ChatFlow = {
  id: 'beneficial-owner-letters-appendix',
  taskCodeMatch: ['M32109'],
  categoryMatch: [
    'beneficial owner letters',
    'beneficial owner letter',
    'bo letters',
    'bo letter',
    'appendix a',
    'appendix b',
    'appendix c',
    'bo appendix',
  ],

  getInitialMessages(preSet: PreSetSelection): ConversationMessage[] {
    const docCount = preSet.documents.length;
    return [
      {
        role: 'ai',
        content:
          `Welcome to **Beneficial Owner Letters — Appendix A, B & C**.\n\n` +
          `On the right, start with a short **survey** to choose which reply form(s) to prepare ` +
          `(Appendix A / B / C). Only selected documents are shown — up to ${docCount || 5} templates may be linked.\n\n` +
          `These forms are separate from s.60C Notices (M32108).\n\n` +
          `Fill highlighted variables — click **✨** to look up a person, ` +
          `use **📅** for dates and **📍** for addresses.\n\n` +
          `When ready, click **Generate** to create the task and documents.\n\n` +
          `First, please enter a **Task Title**.`,
        timestamp: Date.now(),
        stepId: 1,
        source: 'system',
      },
    ];
  },

  steps: [
    {
      id: 'input-title',
      aiMessage: 'Please enter a Task Title.',
      widget: 'text-input',
      widgetConfig: { targetVarKey: '_taskTitle', targetEntityIndex: 0 },
      awaitEvent: true,
    },
    {
      id: 'select-entity',
      aiMessage: 'Search and select the **entity** for these BO letters. You can skip if not linked yet.',
      widget: 'entity-selector',
      widgetConfig: {
        targetVarKey: '_entity',
        targetEntityIndex: 0,
        allowSkip: true,
        analysis: {
          success: true, placeholder: '_entity', sourceResolver: 'ENTITY', officerRole: null,
          varKey: '_entity', varLabel: 'Entity', confidence: 1,
          message: 'Select Entity (Beneficial Owner Letters).',
          relatedFields: [], options: [],
        } as PlaceholderAnalysis,
      },
      awaitEvent: true,
    },
    {
      id: 'confirm',
      aiMessage:
        'Complete the **appendix selection survey** on the right, then fill the selected forms. ' +
        'Use **✨** for beneficial owners / members / senior management, **📅 / 📍** for dates and addresses, then **Generate**.',
      widget: null,
      nextStep: null,
    },
    { id: 'officer-role', aiMessage: 'Please select a role for the person you want to look up.', widget: 'officer-role-buttons', awaitEvent: true },
    { id: 'officer-list', aiMessage: 'Select a person from the list.', widget: 'officer-list', awaitEvent: true },
    { id: 'officer-detail', aiMessage: 'Person details loaded. The document has been pre-filled.', widget: 'officer-field-picker', awaitEvent: true },
    { id: 'address-input', aiMessage: 'Enter the address below.', widget: 'address-input', awaitEvent: true },
    { id: 'address-list', aiMessage: 'This person has multiple addresses. Pick the one to use.', widget: 'address-options', awaitEvent: true },
  ],

  handleEvent(eventName: string, payload: any, context: ChatFlowContext): ChatFlowEventResult {
    if (eventName === 'value-submit' && context.currentStepId === 'input-title') {
      const title = typeof payload === 'string' ? payload : payload?.value || payload;
      if (!title || !String(title).trim()) return {};
      return {
        messages: [{ role: 'user', content: String(title), timestamp: Date.now(), stepId: 1 }],
        formUpdates: { taskTitle: String(title).trim() },
        nextStepId: 'select-entity',
      };
    }

    if (eventName === 'entity-select' && context.currentStepId === 'select-entity') {
      const ent = payload || {};
      const entityCd = ent.entityCd || ent.icpCd || '';
      const entityName = ent.entityName || ent.companyName || 'Entity';
      if (!entityCd) return {};
      return {
        messages: [{ role: 'user', content: `Selected: **${entityName}**`, timestamp: Date.now(), stepId: 1 }],
        formUpdates: {
          entityCd, entityName, icpCd: entityCd,
          registrationNo: ent.registrationNo || '', entitySkipped: false, entityReselect: false,
        },
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'entity-skip' && context.currentStepId === 'select-entity') {
      return {
        messages: [{ role: 'user', content: 'Skipped entity — continue without a linked company.', timestamp: Date.now(), stepId: 1 }],
        formUpdates: { entityCd: '', entityName: '', icpCd: '', registrationNo: '', tlinkData: null, entitySkipped: true },
        nextStepId: 'confirm',
      };
    }

    if (eventName === 'request-person-lookup') {
      const ctx = payload || {};
      const entityCd = context.entityCd || context.formData.entityCd || '';
      if (!ctx.defaultRole) {
        return {
          messages: [{ role: 'user', content: `Look up a person for **${ctx.fieldGroup || 'this field'}**.`, timestamp: Date.now(), stepId: 1 }],
          nextStepId: 'officer-role',
          nextStepWidgetData: { entityCd },
        };
      }
      const roleLabel = ctx.defaultRole === 'SHAREHOLDER' ? 'Member' : ctx.defaultRole;
      const kind = ctx.memberEntityKind === 'corporate' || ctx.memberEntityKind === 'individual'
        ? ctx.memberEntityKind
        : (ctx.docEntityKind === 'corporate' || ctx.docEntityKind === 'individual'
          ? ctx.docEntityKind
          : undefined);
      return {
        messages: [{ role: 'user', content: `Looking up **${roleLabel}**...`, timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'officer-list',
        nextStepWidgetData: {
          entityCd,
          selectedRole: ctx.defaultRole,
          ...(kind ? { memberEntityKind: kind } : {}),
          ...(kind === 'corporate' ? { contactCategoryCd: 'B09001' }
            : kind === 'individual' ? { contactCategoryCd: 'B09002' } : {}),
        },
      };
    }

    if (eventName === 'officer-role-select' && context.currentStepId === 'officer-role') {
      const role = payload;
      const entityCd = context.entityCd || context.formData.entityCd || '';
      return {
        messages: [{ role: 'user', content: `Role: **${role}**`, timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'officer-list',
        nextStepWidgetData: { entityCd, selectedRole: role },
      };
    }

    if (eventName === 'officer-select' && context.currentStepId === 'officer-list') {
      const officer = payload || {};
      const officerName = officer.name || officer.contactName || 'Selected person';
      return {
        messages: [{ role: 'user', content: `Selected person: **${officerName}**`, timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'officer-detail',
      };
    }

    if (eventName === 'officer-list-back' && context.currentStepId === 'officer-list') {
      return { nextStepId: 'officer-role' };
    }

    if (eventName === 'request-address-input') {
      return {
        messages: [{ role: 'user', content: 'Enter an address.', timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'address-input',
      };
    }

    if (eventName === 'request-address-lookup') {
      const ctx = payload || {};
      const opts = Array.isArray(ctx.addresses) ? ctx.addresses : [];
      const subject = ctx.subjectLabel || 'this person';
      return {
        messages: [{ role: 'user', content: `Choose an address for **${subject}**.`, timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'address-list',
        nextStepWidgetData: { addressOptions: opts as any },
      };
    }

    if (eventName === 'address-option-select') {
      const opt = payload || {};
      return {
        messages: [{ role: 'user', content: `Selected: **${opt.label || opt.type}**`, timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'confirm',
      };
    }

    return {};
  },
};
